import React, { useState, useCallback } from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import Select, { SelectProps } from '@material-ui/core/Select';
import { useField } from 'formik';
import { ClassApi } from '../../types/styles';
import { useId } from '../../hooks';
import { GenericItem } from '../../types/items';
import useStyles from './styles';

type Props = Omit<SelectProps, 'classes'> & {
  label?: string;
  name: string;
  hideErrors?: boolean;
  isNone?: boolean;
  classes?: ClassApi<typeof useStyles>;
  items: Array<GenericItem<string | number>> | null;
  disabled?: boolean;
  customOption?: (title: string, key: string) => JSX.Element;
  useBlankSelectBeforeInit?: boolean;
};

const FormSelect = ({
  name,
  label,
  disabled = false,
  hideErrors = false,
  className,
  items,
  isNone = false,
  customOption,
  useBlankSelectBeforeInit = false,
}: Props) => {
  const classes = useStyles();

  const [field, meta] = useField({ name });

  const [value, setValue] = useState(meta.value);

  const changeHandler = useCallback((value) => {
    setValue(value);
  }, [setValue]);

  const selectId = useId('form-select-');
  const errorId = useId('input-error-');

  if (useBlankSelectBeforeInit && !(items && items.length)) {
    return (
      <FormControl
        fullWidth
        disabled
        className={classes.formControl}
      >
        {label
        && (
          <InputLabel
            htmlFor={selectId}
            className={classes.label}
            classes={{
              root: classes.label,
              focused: classes.focused,
            }}
          >
            {label}
          </InputLabel>
        )}
        <Select value="" disabled className={classes.blank} />
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      disabled={disabled}
      className={classes.formControl}
      error={Boolean(meta.touched && meta.error)}
    >
      {label
        && (
          <InputLabel
            htmlFor={selectId}
            className={classes.label}
            classes={{
              root: classes.label,
              focused: classes.focused,
            }}
          >
            {label}
          </InputLabel>
        )}
      <Select
        id={selectId}
        value={value}
        onChange={changeHandler}
        className={classes.root}
        disabled={disabled}
        classes={{
          disabled: classes.disabled,
        }}
        MenuProps={{
          style: {
            zIndex: 2147483100,
          },
        }}
        {...field}
      >
        {isNone
          && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
        {items
          && items.map((item) => (
            <MenuItem value={item.id} key={item.id}>
              {customOption ? customOption(item.title, item.id.toString()) : item.title}
            </MenuItem>
          ))}
      </Select>
      {meta.touched && meta.error && !hideErrors && (
        <FormHelperText id={errorId}>{meta.error}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormSelect;
